// ─── OptimizeAllImagesPanel ─────────────────────────────────────
// Studio-level "Optimize all images" maintenance action.
// Lives under Settings → Performance in EventSetup.
//
// Flow:
//   1. On mount, GET /images/scan to count eligible images + ineligible.
//   2. Designer hits "Optimize all images" → confirmation summary.
//   3. POST /images/optimize with all eligible URLs (excluding the ones
//      already optimized or already below threshold).
//   4. Show success/skip/fail counts, with Undo (10s) + Run mobile check.
//
// Security is delegated to the backend's existing GridFS metadata check.

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Zap, Undo2, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function OptimizeAllImagesPanel({ eventId, onRunMobileCheck, adminDarkMode = false }) {
  const [scan, setScan] = useState(null); // { eligible: [], ineligible: [] }
  const [scanning, setScanning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const [undoVisible, setUndoVisible] = useState(false);

  const runScan = useCallback(async () => {
    if (!eventId) return;
    setScanning(true);
    try {
      const res = await axios.get(
        `${API}/api/events/${eventId}/images/scan`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setScan(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not scan images');
    } finally {
      setScanning(false);
    }
  }, [eventId]);

  useEffect(() => { runScan(); }, [runScan]);

  // Filter the scan result to URLs that are worth optimizing right now.
  // Exclusions: already-optimized (filename contains `_opt_`) and tiny
  // images below the threshold (no win expected).
  const worthOptimizing = (scan?.eligible || []).filter(
    (e) => !e.already_optimized && e.pixels > 1_500_000, // small safety margin under 2.0M cap
  );
  const alreadyOptimizedCount = (scan?.eligible || []).filter((e) => e.already_optimized).length;
  const belowThresholdCount = (scan?.eligible || []).filter(
    (e) => !e.already_optimized && e.pixels <= 1_500_000,
  ).length;

  const runOptimize = useCallback(async () => {
    if (!eventId || running || !worthOptimizing.length) return;
    setRunning(true);
    setConfirming(false);
    try {
      const res = await axios.post(
        `${API}/api/events/${eventId}/images/optimize`,
        { urls: worthOptimizing.map((e) => e.url) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const data = res.data || {};
      const realOpt = (data.optimized || []).filter((o) => !o.skipped && (o.bytes_saved || 0) > 0);
      const savedBytes = realOpt.reduce((acc, o) => acc + (o.bytes_saved || 0), 0);
      const skippedAlreadyEfficient = (data.optimized || []).filter((o) => o.skipped).length;
      setResult({
        count: realOpt.length,
        savedBytes,
        skippedAlreadyEfficient,
        failures: data.failed || [],
        mapping: data.mapping || [],
        rewritesApplied: data.rewrites_applied || 0,
      });
      if (realOpt.length > 0) {
        toast.success(`Optimized ${realOpt.length} image${realOpt.length === 1 ? '' : 's'} · saved ${formatBytes(savedBytes)}`);
        setUndoVisible(true);
        if (undoTimer) clearTimeout(undoTimer);
        const tid = setTimeout(() => setUndoVisible(false), 10000);
        setUndoTimer(tid);
      } else {
        toast.success('All images are already optimized.');
      }
      if ((data.failed || []).length > 0) {
        toast.warning(`Could not optimize ${data.failed.length} image${data.failed.length === 1 ? '' : 's'}`);
      }
      // Refresh scan to reflect the new state.
      runScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Optimize all images failed');
    } finally {
      setRunning(false);
    }
  }, [eventId, running, worthOptimizing, undoTimer, runScan]);

  const runUndo = useCallback(async () => {
    if (!eventId || !result?.mapping?.length) return;
    try {
      await axios.post(
        `${API}/api/events/${eventId}/images/optimize/undo`,
        { mapping: result.mapping },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      toast.success('Reverted batch — original images restored');
      setResult(null);
      setUndoVisible(false);
      if (undoTimer) clearTimeout(undoTimer);
      setUndoTimer(null);
      runScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Undo failed');
    }
  }, [eventId, result, undoTimer, runScan]);

  const labelColor = adminDarkMode ? 'text-gray-500' : 'text-gray-400';
  const muted = adminDarkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = adminDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';

  return (
    <div data-testid="optimize-all-images-panel">
      <h4 className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${labelColor}`}>
        Performance
      </h4>
      <div className={`rounded-lg border p-3 ${cardBg}`}>
        <div className="flex items-start gap-2 mb-2">
          <Zap className="w-4 h-4 mt-0.5 text-indigo-500 flex-shrink-0" />
          <div className="flex-1">
            <div className={`text-sm font-medium ${adminDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              Optimize all images
            </div>
            <p className={`text-[11px] leading-relaxed mt-0.5 ${muted}`}>
              Reduce image weight for faster mobile loading without changing the visual layout.
            </p>
          </div>
        </div>

        {/* Scan summary */}
        {scanning && (
          <div className={`text-[11px] flex items-center gap-1.5 ${muted}`}>
            <Loader2 className="w-3 h-3 animate-spin" /> Scanning images…
          </div>
        )}
        {!scanning && scan && (
          <div className={`text-[11px] space-y-0.5 ${muted}`} data-testid="optimize-all-summary">
            <div>
              {worthOptimizing.length === 0
                ? 'All images are already optimized.'
                : <><strong>{worthOptimizing.length}</strong> image{worthOptimizing.length === 1 ? '' : 's'} eligible for optimization.</>}
            </div>
            {(alreadyOptimizedCount + belowThresholdCount) > 0 && (
              <div className="opacity-70">
                {alreadyOptimizedCount + belowThresholdCount} already efficient.
              </div>
            )}
          </div>
        )}

        {/* Action area */}
        {!result && !confirming && (
          <button
            onClick={() => setConfirming(true)}
            disabled={scanning || running || worthOptimizing.length === 0}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="optimize-all-trigger-btn"
          >
            <Zap className="w-3.5 h-3.5" /> Optimize all images
          </button>
        )}

        {/* Confirmation */}
        {confirming && !result && (
          <div className={`mt-2 p-2 rounded-md border ${adminDarkMode ? 'border-indigo-900 bg-indigo-950/40' : 'border-indigo-100 bg-indigo-50'}`} data-testid="optimize-all-confirm">
            <p className={`text-[11px] leading-relaxed mb-2 ${adminDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>
              Optimize {worthOptimizing.length} image{worthOptimizing.length === 1 ? '' : 's'}? Layout and cropping will not change.
            </p>
            <div className="flex gap-1.5">
              <button
                onClick={runOptimize}
                disabled={running}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                data-testid="optimize-all-confirm-btn"
              >
                {running ? <><Loader2 className="w-3 h-3 animate-spin" /> Optimizing…</> : 'Optimize all images'}
              </button>
              <button
                onClick={() => setConfirming(false)}
                disabled={running}
                className={`px-2 py-1 rounded text-[11px] font-medium ${adminDarkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'} disabled:opacity-50`}
                data-testid="optimize-all-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mt-2 space-y-1.5" data-testid="optimize-all-result">
            <div className={`text-[11px] flex items-start gap-1.5 ${result.count > 0 ? 'text-emerald-600' : muted}`}>
              <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <div>
                {result.count > 0
                  ? <>Optimized <strong>{result.count}</strong> image{result.count === 1 ? '' : 's'} · saved <strong>{formatBytes(result.savedBytes)}</strong></>
                  : 'No images needed optimization.'}
                {result.skippedAlreadyEfficient > 0 && (
                  <div className="opacity-70 mt-0.5">
                    Skipped {result.skippedAlreadyEfficient} already efficient image{result.skippedAlreadyEfficient === 1 ? '' : 's'}.
                  </div>
                )}
              </div>
            </div>
            {result.failures.length > 0 && (
              <div className="text-[11px] flex items-start gap-1.5 text-amber-600">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <div>
                  {result.failures.length} could not be optimized — reasons: {result.failures.map((f) => f.reason).slice(0, 3).join(', ')}.
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 mt-1">
              {undoVisible && result.mapping.length > 0 && (
                <button
                  onClick={runUndo}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium ${adminDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                  data-testid="optimize-all-undo-btn"
                >
                  <Undo2 className="w-3 h-3" /> Undo
                </button>
              )}
              {onRunMobileCheck && (
                <button
                  onClick={onRunMobileCheck}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium ${adminDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                  data-testid="optimize-all-run-check-btn"
                >
                  Run mobile check
                </button>
              )}
              <button
                onClick={() => { setResult(null); runScan(); }}
                className={`inline-flex items-center px-2 py-1 rounded text-[11px] font-medium underline opacity-70 hover:opacity-100 ${muted}`}
                data-testid="optimize-all-dismiss-btn"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
